import { Label } from "@radix-ui/react-menu"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { Slot } from "@radix-ui/react-slot"
import { cva, VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

interface ListProps extends React.HTMLAttributes<HTMLUListElement> {
  asChild?: boolean
}
export const List = ({ className, children, asChild, ...props }: ListProps & { ref?: React.Ref<HTMLUListElement> }) => {
  const Comp = asChild ? Slot : "ul"

  return (
    <Comp className={cn("list-none space-y-0.5 overflow-hidden", className)} {...props}>
      {children}
    </Comp>
  )
}

interface ListItemProps extends React.HTMLAttributes<HTMLLIElement> {
  asChild?: boolean
}
export const ListItem = ({
  className,
  children,
  asChild,
  ...props
}: ListItemProps & { ref?: React.Ref<HTMLLIElement> }) => {
  const Comp = asChild ? Slot : "li"

  return (
    <Comp
      className={cn(
        "group disabled:text-on-surface-disabled flex w-full min-w-0 overflow-hidden rounded-md px-2 py-1.5 text-left",
        className,
      )}
      {...props}>
      {children}
    </Comp>
  )
}

const listTextVariants = cva("group-disabled:text-on-surface-disabled min-w-0", {
  variants: {
    variant: {
      primary: "text-on-surface truncate text-sm leading-5",
      secondary: "text-on-surface-subdued text-xs leading-4 tracking-[0.25px]",
    },
  },
  defaultVariants: {
    variant: "primary",
  },
})

interface ListTextProps extends React.HTMLAttributes<HTMLParagraphElement>, VariantProps<typeof listTextVariants> {
  asChild?: boolean
}
export const ListText = (props: ListTextProps & { ref?: React.Ref<HTMLParagraphElement> }) => {
  const { className, children, asChild, variant, ...rest } = props
  const Comp = asChild ? Slot : "p"

  return (
    <Comp className={cn(listTextVariants({ variant }), className)} {...rest}>
      {children}
    </Comp>
  )
}

export const ListLabel = ({ className, ...props }: React.ComponentPropsWithRef<typeof Label>) => (
  <Label className={cn("text-on-surface-subdued mt-2 pl-2 text-sm leading-5", className)} {...props} />
)

interface ListDividerProps extends React.ComponentPropsWithRef<"hr"> {
  className?: string
}
export const ListDivider = ({ className, ...props }: ListDividerProps) => (
  <hr className={cn("my-2", className)} {...props} />
)

export const ListRadioGroup = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof RadioGroupPrimitive.Root>) => {
  return <RadioGroupPrimitive.Root className={cn("radio-group grid", className)} {...props} />
}

interface ListRadioItemProps extends React.ComponentPropsWithRef<typeof RadioGroupPrimitive.Item> {
  error?: boolean
  classNameRadio?: string
  subComponent?: React.ReactNode
}
export const ListRadioItem = (props: ListRadioItemProps) => {
  const { className, children, error, classNameRadio, subComponent = null, ...rest } = props
  return (
    <RadioGroupPrimitive.Item
      {...rest}
      className={cn(
        "list-radio-item aria-selected:bg-primary-surface data-selected:bg-primary-surface flex min-h-12 items-center gap-2 rounded-md p-1 px-2",
        error && "radio-item--error",
        className,
      )}>
      {children}
      <div className={cn("hover:bg-surface-hovered ml-auto flex gap-1 rounded-full p-2", classNameRadio)}>
        <div className="list-radio-check relative">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.25C10.0716 2.25 8.18657 2.82183 6.58319 3.89317C4.97982 4.96451 3.73013 6.48726 2.99218 8.26884C2.25422 10.0504 2.06114 12.0108 2.43735 13.9021C2.81355 15.7934 3.74215 17.5307 5.10571 18.8943C6.46928 20.2579 8.20656 21.1865 10.0979 21.5627C11.9892 21.9389 13.9496 21.7458 15.7312 21.0078C17.5127 20.2699 19.0355 19.0202 20.1068 17.4168C21.1782 15.8134 21.75 13.9284 21.75 12C21.7473 9.41498 20.7192 6.93661 18.8913 5.10872C17.0634 3.28084 14.585 2.25273 12 2.25ZM12 20.25C10.3683 20.25 8.77326 19.7661 7.41655 18.8596C6.05984 17.9531 5.00242 16.6646 4.378 15.1571C3.75358 13.6496 3.5902 11.9908 3.90853 10.3905C4.22685 8.79016 5.01259 7.32015 6.16637 6.16637C7.32016 5.01259 8.79017 4.22685 10.3905 3.90852C11.9909 3.59019 13.6497 3.75357 15.1571 4.37799C16.6646 5.00242 17.9531 6.05984 18.8596 7.41655C19.7661 8.77325 20.25 10.3683 20.25 12C20.2475 14.1873 19.3775 16.2843 17.8309 17.8309C16.2843 19.3775 14.1873 20.2475 12 20.25Z" />
          </svg>
          <RadioGroupPrimitive.Indicator asChild className="radio-indicator flex items-center justify-center">
            <span className="bg-primary absolute top-[50%] left-[50%] h-3 w-3 rounded-full" />
          </RadioGroupPrimitive.Indicator>
        </div>
      </div>
      {subComponent}
    </RadioGroupPrimitive.Item>
  )
}
